import {
  ChartDataset,
  ChartStyle
} from "state/ui/editor/elements/ChartElement";
import {
  bandScale,
  datasetDomain,
  defaultPlotArea,
  formatTickLabel,
  generateTicks,
  linearScale,
  seriesColorAt
} from "../chartScales";
import CartesianAxes from "./CartesianAxes";

interface Props {
  dataset: ChartDataset;
  style: ChartStyle;
  width: number;
  height: number;
}

const AreaChart = ({ dataset, style, width, height }: Props) => {
  const plot = defaultPlotArea(width, height, {
    showLegend: style.showLegend,
    showAxes: style.showAxes,
    fontSize: style.fontSize
  });

  const domain = datasetDomain(dataset);
  const y = linearScale(domain, [plot.top + plot.height, plot.top]);
  const x = bandScale(dataset.headers, [plot.left, plot.left + plot.width]);
  const ticks = generateTicks(domain[0], domain[1]);
  const baseline = y.scale(0);

  const xCenter = (index: number) => x.scaleAt(index) + x.bandwidth / 2;

  return (
    <g>
      <CartesianAxes
        dataset={dataset}
        style={style}
        plot={plot}
        x={x}
        y={y}
        ticks={ticks}
      />

      {dataset.rows.map((row, rowIndex) => {
        const color = seriesColorAt(style.seriesColors, rowIndex);
        const points = row.values
          .map((value, valueIndex) => {
            if (valueIndex >= dataset.headers.length) return null;
            return { x: xCenter(valueIndex), y: y.scale(value), value };
          })
          .filter((p): p is { x: number; y: number; value: number } =>
            Boolean(p)
          );

        if (points.length === 0) return null;

        const linePath = points
          .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
          .join(" ");

        const firstPoint = points[0]!;
        const lastPoint = points[points.length - 1]!;
        const areaPath = `${linePath} L ${lastPoint.x} ${baseline} L ${firstPoint.x} ${baseline} Z`;

        return (
          <g key={`series-${rowIndex}`}>
            <path d={areaPath} fill={color} fillOpacity={0.35} />
            <path
              d={linePath}
              stroke={color}
              strokeWidth={2}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {style.showDataLabels &&
              points.map((p, i) => (
                <text
                  key={`label-${rowIndex}-${i}`}
                  x={p.x}
                  y={p.y - 6}
                  fontSize={style.fontSize}
                  fill={style.color}
                  fontFamily={style.fontFamily}
                  textAnchor="middle"
                >
                  {formatTickLabel(p.value)}
                </text>
              ))}
          </g>
        );
      })}
    </g>
  );
};

export default AreaChart;
